// A toolkit as the page says it (02-DESIGN/03-console/01-the-console.md §
// the words: *acts as you* · *acts as Acme* · *public* for the three scopes;
// *Link* · *Unlink*; *Connect* for a pasted key) and where this person
// stands with it (02-DESIGN/08-toolkits/03-linking.md § discovering what
// needs linking): for a person-scoped toolkit, linked or configured against
// the person's own paths, and the one act the scheme derives — a link, a
// form, or nothing; a shared or public toolkit is configured by definition;
// a toolkit that says nothing about its setup is reported as unknown, never
// a guessed form.

import type { Tone } from "../../ui/components.tsx";
import { isLinked, Scheme, Scope, type Toolkit } from "../../wire/toolkit.ts";

/** scopeWords is what a scope is called on the page. */
export function scopeWords(scope: string, workspace: string): string {
  switch (scope) {
    case Scope.person:
      return "acts as you";
    case Scope.tenant:
      return `acts as ${workspace}`;
    case Scope.none:
      return "public";
    default:
      return "unknown";
  }
}

/** schemeWords is what a toolkit's sign-in is called on the page: what it needs from the person. */
export function schemeWords(tk: Pick<Toolkit, "scope" | "scheme">, workspace: string): string {
  if (tk.scope === Scope.none) {
    return "nothing: the source is public";
  }
  if (tk.scope === Scope.tenant) {
    return `one key for everyone in ${workspace}, set by an admin`;
  }
  switch (tk.scheme) {
    case Scheme.oauthRefresh:
      return "your own account at the provider, linked once";
    case Scheme.token:
      return "a key of your own, pasted once";
    default:
      return "not stated";
  }
}

/** Standing is where this person stands with a toolkit. */
export type Standing = "linked" | "not-linked" | "connected" | "not-connected" | "ready" | "unknown";

/**
 * standingOf reads the standing: linked or connected when the person's own
 * path for the toolkit is among those listed, ready for a shared or public
 * toolkit, unknown for one that says nothing about its setup.
 */
export function standingOf(tk: Toolkit, linked: ReadonlySet<string>, principal: string): Standing {
  if (tk.scope === "" || tk.scheme === "") {
    return "unknown";
  }
  if (tk.scope !== Scope.person) {
    return "ready";
  }
  const has = principal !== "" && isLinked(linked, principal, tk);
  switch (tk.scheme) {
    case Scheme.oauthRefresh:
      return has ? "linked" : "not-linked";
    case Scheme.token:
      return has ? "connected" : "not-connected";
    default:
      return "unknown";
  }
}

/** standingWords is a standing as the page says it. */
export function standingWords(s: Standing): { word: string; tone: Tone } {
  switch (s) {
    case "linked":
      return { word: "linked", tone: "live" };
    case "connected":
      return { word: "connected", tone: "live" };
    case "not-linked":
      return { word: "not linked", tone: "neutral" };
    case "not-connected":
      return { word: "not connected", tone: "neutral" };
    case "ready":
      return { word: "ready", tone: "ok" };
    case "unknown":
      return { word: "setup unknown", tone: "bad" };
  }
}

/** Act is the one act a standing offers. */
export type Act = "link" | "unlink" | "connect" | "disconnect";

/** actOf is the act the scheme derives from the standing; nothing for a toolkit configured by definition or unknown. */
export function actOf(s: Standing): Act | undefined {
  switch (s) {
    case "not-linked":
      return "link";
    case "linked":
      return "unlink";
    case "not-connected":
      return "connect";
    case "connected":
      return "disconnect";
    default:
      return undefined;
  }
}

/**
 * UNLINK_CONSEQUENCE is the plain sentence unlinking binds (03-linking.md §
 * unlinking): deleting the stored credential is the half the console
 * performs; revoking at the provider is the person's, at the provider's own
 * account page.
 */
export const UNLINK_CONSEQUENCE =
  "The stored credential is deleted and this toolkit stops acting as you. This does not withdraw the access you granted at the provider; do that in your provider account.";

/** DISCONNECT_CONSEQUENCE is the sentence for a pasted key. */
export const DISCONNECT_CONSEQUENCE =
  "The stored key is deleted and this toolkit stops acting as you. The key itself stays valid at the provider until you withdraw it there.";

/** SetupField is one field of the record a token toolkit's setup schema describes. */
export interface SetupField {
  name: string;
  title: string;
  required: boolean;
}

/**
 * setupFields reads the fields of a setup schema: an object schema's string
 * properties, in order, with their titles. A schema that is not that, or
 * not JSON, is unknown and no form is guessed.
 */
export function setupFields(schema: string | undefined): SetupField[] | undefined {
  if (schema === undefined || schema === "") {
    return undefined;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(schema);
  } catch {
    return undefined;
  }
  if (typeof parsed !== "object" || parsed === null) {
    return undefined;
  }
  const o = parsed as Record<string, unknown>;
  if (o.type !== "object" || typeof o.properties !== "object" || o.properties === null) {
    return undefined;
  }
  const required = new Set(
    Array.isArray(o.required) ? (o.required as unknown[]).filter((r) => typeof r === "string") : [],
  );
  const out: SetupField[] = [];
  for (const [name, prop] of Object.entries(o.properties as Record<string, unknown>)) {
    if (typeof prop !== "object" || prop === null) {
      return undefined;
    }
    const p = prop as Record<string, unknown>;
    if (p.type !== undefined && p.type !== "string") {
      return undefined;
    }
    out.push({
      name,
      title: typeof p.title === "string" && p.title !== "" ? p.title : name,
      required: required.has(name),
    });
  }
  return out.length === 0 ? undefined : out;
}
